import React from 'react';
import { Link } from 'react-router-dom';

const AffiliateDisclosure = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div
        className="py-14 px-6 text-white"
        style={{
          background: 'linear-gradient(135deg, #F06F6B 0%, #F07A5E 40%, #F58A54 100%)',
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-1">Affiliate Disclosure</h1>
          <p className="text-white/90 text-base font-medium mb-1">Atlas Procurement Services, LLC DBA Stackd</p>
          <p className="text-white/80 text-sm">Last Updated: 3/12/2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">1. Overview</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd ("Stackd Financial," "we," "our," or "us") operates saveonstackd.com, a platform designed to help consumers research, compare, and make informed decisions about major purchases, financial products, services, and life expenses.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some links and recommendations displayed on Stackd may be affiliate links or referral links. This means that if you click a link and take certain actions—such as making a purchase, requesting a quote, or applying for a product—we may receive compensation from the company or provider.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">This compensation may include:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Affiliate commissions</li>
              <li>Referral fees</li>
              <li>Lead generation fees</li>
              <li>Advertising payments</li>
              <li>Revenue-sharing arrangements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              These payments help support the development, maintenance, and operation of the Stackd platform.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">2. Our Approach to Recommendations</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd is designed to help users organize research, compare options, and make more informed purchasing decisions.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              While we may receive compensation from certain partners, we strive to present information and tools that help users evaluate options based on their own preferences and needs.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Compensation may influence:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Which companies or services are available through the platform</li>
              <li>Which partners we integrate with</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              However, we strive to present multiple options whenever possible so users can make independent decisions.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Recommendations are not blocked or de-listed due to lack of compensation. Stackd is a personal finance tool designed to provide the best available options, given a set of user driven criteria. Whether the product/service has compensation tied to it is irrelevant in the recommendation process.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Sponsored advertisements will be clearly defined and the user will have the ability to choose to quote with a sponsored vendor or not.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">3. Third-Party Websites and Transactions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Stackd generally does not sell products or services directly.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you click certain links within Stackd, you may be redirected to third-party websites where you can:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Purchase products</li>
              <li>Apply for financial products</li>
              <li>Request quotes</li>
              <li>Schedule services</li>
              <li>Submit information to providers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              These transactions occur entirely on third-party platforms, and Stackd does not control those websites, their policies, or their services.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We encourage users to review the terms and privacy policies of any third-party website before completing a transaction or submitting personal information.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">4. Accuracy of Offers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Any pricing, rates, promotions, or offers displayed on Stackd are provided by third-party companies and may change at any time.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">Stackd Financial does not guarantee:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>Product availability</li>
              <li>Eligibility for offers or financing</li>
              <li>Approval for financial products</li>
              <li>Final pricing or service terms</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              All final terms are determined by the third-party provider.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">5. Sponsored Listings and Advertising</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some companies may pay for enhanced placement or promotional visibility on the Stackd platform.
            </p>
            <p className="text-gray-700 leading-relaxed mb-3">
              Sponsored or promoted placements may be labeled with terms such as:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 pl-2 mb-4">
              <li>"Sponsored"</li>
              <li>"Featured"</li>
              <li>"Partner"</li>
              <li>"Promoted"</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              These labels are intended to provide transparency regarding paid placements.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-lg font-bold text-gray-900 mb-3">6. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you have questions about this disclosure, please contact:
            </p>
            <p className="text-gray-700 leading-relaxed">
              Email:{' '}
              <a
                href="mailto:info@saveonstackd.com"
                className="text-orange-500 hover:text-orange-600 transition-colors"
              >
                info@saveonstackd.com
              </a>
            </p>
          </section>

          {/* Back link */}
          <div className="mt-10 pt-6 border-t border-gray-100">
            <Link
              to="/"
              className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDisclosure;
